import { Component, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  AbstractControl,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';

/**
 * FormField Component
 * Reusable form field component with label, input, and error message
 * Uses PrimeNG InputText for styling and responsiveness
 * Supports reactive forms and template-driven forms
 */
@Component({
  selector: 'app-form-field',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, InputTextModule],
  templateUrl: './form-field.html',
  styleUrl: './form-field.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormField),
      multi: true,
    },
  ],
})
export class FormField implements ControlValueAccessor {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() type: string = 'text';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() errorMessage: string = '';
  @Input() showError: boolean | null | undefined = false;
  @Input() formControlName: string = '';
  @Input() formControl?: AbstractControl | null;

  // Custom styling
  @Input() labelClass: string = 'block text-gray-700 text-sm font-medium';
  @Input() inputClass: string =
    'w-full h-10 px-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-botmaaOrange';
  @Input() errorClass: string = 'text-red-600 text-xs';
  @Input() containerClass: string = '';

  // Responsive classes
  @Input() responsiveLabelClass: string = '';
  @Input() responsiveInputClass: string = '';

  value: string = '';
  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
    this.onChange(this.value);
    this.onTouched();
  }

  onFormControlInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (this.formControl) {
      this.formControl.setValue(target.value);
      this.formControl.markAsTouched();
    }
  }

  /**
   * Get combined label classes
   */
  getLabelClasses(): string {
    return [this.labelClass, this.responsiveLabelClass].filter(Boolean).join(' ');
  }

  /**
   * Get combined input classes
   */
  getInputClasses(): string {
    return [this.inputClass, this.responsiveInputClass].filter(Boolean).join(' ');
  }
}
